import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const ReportingLoadingState: React.FC = () => {
  return (
    <div className="h-full w-full bg-gray-50 overflow-hidden flex flex-col">
      <div className="flex-grow overflow-auto p-4 md:p-6 m-3 bg-background text-foreground border border-gray-100 rounded-xl">
        {/* Header Skeleton */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
          <div className="space-y-2">
            <div className="h-8 w-56 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="h-9 w-32 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Filters Skeleton */}
        <Card className="mb-6">
          <Accordion type="single" collapsible defaultValue="" className="w-full">
            <AccordionItem value="filters" className="border-none">
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
              </AccordionTrigger>
              <AccordionContent>
                <div className="px-6 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Period Skeleton */}
                    <div className="space-y-2">
                      <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                      <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
                    </div>

                    {/* Date Range Skeleton */}
                    <div className="space-y-2">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                      <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
                    </div>

                    {/* Customer Skeleton */}
                    <div className="space-y-2">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                      <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
                    </div>

                    {/* Site Skeleton */}
                    <div className="space-y-2">
                      <div className="h-4 w-12 bg-gray-200 rounded animate-pulse" />
                      <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
                    </div>

                    {/* Warehouse Skeleton */}
                    <div className="space-y-2">
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                      <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
                    </div>

                    {/* Buttons Skeleton */}
                    <div className="space-y-2 flex items-end gap-2">
                      <div className="flex-1 h-9 bg-gray-200 rounded animate-pulse" />
                      <div className="flex-1 h-9 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>

        {/* Tab Navigation Skeleton */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-9 w-24 bg-gray-200 rounded animate-pulse"
            />
          ))}
        </div>

        {/* Content Skeleton - Generic structure that works for all tabs */}
        <div className="space-y-6">
          {/* Statistics Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
              >
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>

          {/* Graph/Chart Skeleton */}
          <Card>
            <CardHeader>
              <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="w-full h-64 bg-gray-100 rounded animate-pulse" />
            </CardContent>
          </Card>

          {/* Table Skeleton */}
          <Card>
            <CardHeader>
              <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Table Header Skeleton */}
                <div className="bg-gray-50 border-b border-gray-200">
                  <div className="flex gap-4 p-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="flex-1 h-4 bg-gray-200 rounded animate-pulse"
                      />
                    ))}
                  </div>
                </div>

                {/* Table Rows Skeleton */}
                <div className="divide-y divide-gray-200">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex gap-4 p-4">
                      {[1, 2, 3, 4, 5].map((j) => (
                        <div
                          key={j}
                          className="flex-1 h-4 bg-gray-100 rounded animate-pulse"
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReportingLoadingState;
